import { Component } from "react"

import { LazyPdfViewer } from "@/features/chat/components/lazy-pdf-viewer"
import { DocxViewer } from "@/features/file-viewer/components/docx-viewer"
import { ExcelViewer } from "@/features/file-viewer/components/excel-viewer"
import { FallbackViewer } from "@/features/file-viewer/components/fallback-viewer"
import { HtmlViewer } from "@/features/file-viewer/components/html-viewer"
import { MarkdownViewer } from "@/features/file-viewer/components/markdown-viewer"
import { TextViewer } from "@/features/file-viewer/components/text-viewer"

type LocalFilePreviewContentProps = {
  file: File
}

type LocalFilePreviewContentState = {
  objectUrl: string | null
}

function getFileExtension(fileName: string): string {
  return fileName.split(".").pop()?.toLowerCase() ?? ""
}

export class LocalFilePreviewContent extends Component<
  LocalFilePreviewContentProps,
  LocalFilePreviewContentState
> {
  state: LocalFilePreviewContentState = {
    objectUrl: null,
  }

  componentDidMount() {
    const { file } = this.props
    this.createObjectUrl(file)
  }

  componentDidUpdate(previousProps: LocalFilePreviewContentProps) {
    const { file } = this.props

    if (previousProps.file !== file) {
      this.revokeObjectUrl()
      this.createObjectUrl(file)
    }
  }

  componentWillUnmount() {
    this.revokeObjectUrl()
  }

  private createObjectUrl(file: File) {
    this.setState({ objectUrl: URL.createObjectURL(file) })
  }

  private revokeObjectUrl() {
    const { objectUrl } = this.state

    if (objectUrl) {
      URL.revokeObjectURL(objectUrl)
    }
  }

  render() {
    const { file } = this.props
    const { objectUrl } = this.state
    const extension = getFileExtension(file.name)

    if (!objectUrl) {
      return null
    }

    if (extension === "pdf") {
      return <LazyPdfViewer fileUrl={objectUrl} />
    }

    if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(extension)) {
      return (
        <div className="flex h-full min-h-0 items-center justify-center overflow-auto p-4">
          <img alt={file.name} className="max-h-full max-w-full object-contain" src={objectUrl} />
        </div>
      )
    }

    if (extension === "docx") {
      return <DocxViewer fileUrl={objectUrl} />
    }

    if (["xlsx", "xls"].includes(extension)) {
      return <ExcelViewer fileUrl={objectUrl} />
    }

    if (extension === "html") {
      return <HtmlViewer fileUrl={objectUrl} />
    }

    if (extension === "md") {
      return <MarkdownViewer fileUrl={objectUrl} />
    }

    if (["txt", "json", "csv"].includes(extension)) {
      return <TextViewer fileUrl={objectUrl} />
    }

    return <FallbackViewer fileUrl={objectUrl} />
  }
}
